import { push } from '@lagunovsky/redux-react-router'
import HomeAction from 'home/actions/HomeAction'
import { isUndefined, orderBy } from 'lodash'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { hasValue } from 'utils/NumberUtil'
import { H_MAT_CENTRAL } from '../../../../account/constants/AccessRulesConstants'
import { PATH_MATERIEL_CENTRAL } from '../../../../home/constants/RouteConstants'
import { getDate } from '../../../../utils/DateUtil'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { createIconMaterialAssignment, createIconMaterialState } from '../../../../utils/MaterielUtils'
import { getLabel } from '../../../../utils/StoreUtils'
import { normalizeString } from '../../../../utils/StringUtil'
import { CENTRAL, LIST_PANEL, MAP_PANEL, MAT_HEADERS } from '../../../constants/MaterielConstants'
import MaterielFilterForm from '../../filterPanel/MaterielFilterForm'
import MaterielsMapComponent from '../../map/MaterielsMapComponent'
import CentralAction from '../actions/CentralAction'
import { Grid2 } from '@mui/material'
import { useLocation } from 'react-router'
import { DevicesOther } from '@mui/icons-material'
import useTitle from 'utils/customHook/useTitle'
import ReferencialAppList from 'referencial/components/ReferencialAppList'
import { STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import { MATERIEL_STATE_HS_ID, MATERIEL_STATE_ON_SITE_ID, MATERIEL_STATE_REFORMED_ID, MATERIEL_STATE_TRANSFERT_ID } from 'materiel/constants/MaterielStateConstants'

const CentralsApp = ({}) => {
    const [view, setView] = useState(LIST_PANEL)

    const {
        centrals,
        centralsLastSituations,
        centralTypes,
        piezometers,
        pluviometers,
        qualitometers,
        hydrometricStations,
        installations,
        materielStates,
        cities,
        contributors,
        globalResearch,
    } = useSelector(store => ({
        centrals: store.CentralReducer.centrals,
        centralsLastSituations: store.CentralReducer.centralsLastSituations,
        centralTypes: store.CentralReducer.centralTypes,
        piezometers: store.PiezometryReducer.piezometersLight,
        pluviometers: store.PluviometryReducer.pluviometers,
        qualitometers: store.QualityReducer.qualitometersLight,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        installations: store.InstallationReducer.installations,
        materielStates: store.MaterielReducer.materielStates,
        cities: store.CityReducer.cities,
        contributors: store.ContributorReducer.contributors,
        globalResearch: store.HomeReducer.globalResearch,
    }), shallowEqual)

    const dispatch = useDispatch()
    const location = useLocation()

    const [filter, setFilter] = useState(() => {
        const matType = queryString.parse(location.search)?.idType
        return {
            matType: isUndefined(matType) ? undefined : parseInt(matType),
            searchValue: globalResearch,
        }
    })

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_CENTRAL)) {
            dispatch(push('/unauthorized'))
            return
        }
        dispatch(HomeAction.setHelpLink('materiel', ''))
        dispatch(CentralAction.fetchCentrals(true)).then(() => {
            dispatch(CentralAction.fetchCentralTypes())
        })
        HomeAction.updateGlobalResearch('')
        CentralAction.resetCentral()
    }, [])

    useTitle(() => (
        [{
            title: i18n.materiel,
            href: 'materiel',
        }, {
            title: i18n.centrals,
            href: 'materiel/central',
        }]
    ))

    const getHash = mat => normalizeString(MAT_HEADERS.map(key => mat[key]).join(''))

    const getExportData = (materiels) => {
        if (!materiels.length) {
            return []
        }
        const [head, ...tail] = materiels
        return [{ ...head, headers: MAT_HEADERS }, ...tail]
    }

    const getCentralType = () =>
        centralTypes.filter(({ id, label }) => !!centrals
            .find(({ centralType }) => centralType === id) && label)
            .map(({ id, label = '' }) => ({
                id,
                label,
            }))

    const getCorrespondingSite = ({ siteType, siteCode }) => {
        switch (siteType) {
            case STATION_TYPE_CONSTANT.PIEZOMETER:
                return piezometers.find(({ id }) => id === siteCode)
            case STATION_TYPE_CONSTANT.pluviometer:
                return pluviometers.find(({ id }) => id === siteCode)
            case STATION_TYPE_CONSTANT.QUALITOMETER:
                return qualitometers.find(({ id }) => id === siteCode)
            case STATION_TYPE_CONSTANT.HYDROMETRIC_STATION:
                return hydrometricStations.find(({ id }) => id === siteCode)
            case STATION_TYPE_CONSTANT.INSTALLATION:
                return installations.find(({ id }) => id === siteCode)
            default:
                return {}
        }
    }

    const materiels = centrals.map(mat => {
        const lastSituation = centralsLastSituations.find(s => s.idCentral === mat.id)
        const labelState = lastSituation ? getLabel(materielStates, lastSituation.statusCode) : ''
        const site = lastSituation && lastSituation.statusCode === MATERIEL_STATE_ON_SITE_ID && lastSituation.siteType && lastSituation.siteCode && getCorrespondingSite(lastSituation) || {}
        const labelAssignment = (lastSituation && lastSituation.statusCode === MATERIEL_STATE_ON_SITE_ID && lastSituation.siteName) || site.code || ''
        const city = site.townCode && cities.find((o) => o.code === site.townCode) || undefined
        const cityLabel = city ? `${city.name} - [${city.code}]` : (site.townCode || '')
        const departmentLabel = city && city.departmentNumber ? city.departmentNumber : ''
        return {
            materielId: mat.id,
            type: getLabel(centralTypes, mat.materielType),
            manufacturer: getLabel(contributors, mat.manufacturerId, 'labelDisplay', 'id'),
            state: labelState,
            statusCode: lastSituation && lastSituation.statusCode,
            stateDate: lastSituation && getDate(lastSituation.situationDate),
            city: cityLabel,
            administratorId: mat.administrator,
            serialNumber: mat.serialNumber,
            immoNum: mat.reference,
            assignment: labelAssignment,
            siteType: lastSituation && lastSituation.siteType,
            departmentShort: departmentLabel,
            purchaseDate: getDate(mat.purchaseDate),
            manufacturerId: mat.manufacturerId,
            networkCode: mat.networkCode,
            materielType: mat.materielType,
            isEquipment: mat.isEquipment,
            siteCode: lastSituation && lastSituation.siteCode,
            administrator: getLabel(contributors, mat.administrator, 'labelDisplay', 'code'),
        }
    })
    const filterEquipment = !filter.displayEquipment ? materiels.filter(({ isEquipment }) => !isEquipment) : materiels
    const filterAdministrator = filter.administrator ? filterEquipment.filter(({ administratorId }) => filter.administrator === administratorId) : filterEquipment
    const filterManufacturer = filter.manufacturer ? filterAdministrator.filter(({ manufacturerId }) => filter.manufacturer === manufacturerId) : filterAdministrator
    const filterStatusCode = hasValue(filter.state) ? filterManufacturer.filter(({ statusCode }) => statusCode === filter.state) : filterManufacturer
    const filterType = filter.matType ? filterStatusCode.filter(({ materielType }) => materielType === filter.matType) : filterStatusCode
    const filterNetwork = filter.network ? filterType.filter(({ networkCode }) => filter.network === networkCode) : filterType
    const filterOutOfService = filter.displayOutOfService ? filterNetwork : filterNetwork.filter(({ statusCode }) => statusCode !== MATERIEL_STATE_HS_ID && statusCode !== MATERIEL_STATE_REFORMED_ID && statusCode !== MATERIEL_STATE_TRANSFERT_ID)
    const searchValueFormated = normalizeString(filter.searchValue)
    const filteredMateriels = filter.searchValue ? filterOutOfService.filter(mat => getHash(mat).includes(searchValueFormated)) : filterOutOfService

    const getPanel = () => {
        if (view === MAP_PANEL) {
            return (
                <MaterielsMapComponent
                    materielLink={PATH_MATERIEL_CENTRAL}
                    materiels={filteredMateriels}
                />
            )
        }
        const formattedCentrals = filteredMateriels.map(mat => ({
            ...mat,
            state: createIconMaterialState(mat.state, mat.statusCode),
            assignment: mat.statusCode === MATERIEL_STATE_ON_SITE_ID && createIconMaterialAssignment(mat.assignment, mat.siteType) || '',
            nullValue: mat.isEquipment && <DevicesOther sx={{ fontSize: '1.5rem' }} />,
        }))
        return (
            <ReferencialAppList
                title={i18n.centrals}
                useNewTable
                data={formattedCentrals}
                exportData={getExportData(formattedCentrals)}
                type={{ headers: ['nullValue', ...MAT_HEADERS] }}
                newAction={() => dispatch(push('/materiel/central/new'))}
                showNewButton
                showPurgeButton={false}
                setTitleAction={HomeAction.setTitle([{
                    title: i18n.materiel,
                    href: 'materiel',
                }, {
                    title: i18n.centrals,
                    href: 'materiel/central',
                }])}
                onClick={({ materielId }) => dispatch(push(`/materiel/central/${materielId}`))}
                searchable={false}
            />
        )
    }

    return (
        <Grid2 container spacing={1}>
            <Grid2 size={12}>
                <MaterielFilterForm
                    view={view}
                    filter={filter}
                    changeView={newView => setView(newView)}
                    materiels={materiels}
                    typeList={orderBy(getCentralType(), 'label')}
                    onValidate={newFilter => setFilter(newFilter)}
                    materielType={CENTRAL}
                />
            </Grid2>
            <Grid2 size={12}>
                {getPanel()}
            </Grid2>
        </Grid2>
    )
}

export default CentralsApp
